import React, { useEffect, useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import { api } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import type { EventType } from "@/types";

export interface Filters {
  area: string; currency: "" | "IQD" | "USD" | "free"; price_min: string; price_max: string;
  audience: string; category: string; program_type: string; sort: "date" | "price";
}
export const EMPTY_FILTERS: Filters = { area: "", currency: "", price_min: "", price_max: "", audience: "", category: "", program_type: "", sort: "date" };

const sel = "h-10 rounded-full border bg-card px-3 text-sm font-semibold";
const money = "h-10 w-28 rounded-full border bg-card px-3 text-sm";

export const badRange = (f: Filters) => !!f.price_min && !!f.price_max && Number(f.price_min) > Number(f.price_max);

export const filterParams = (f: Filters) => {
  const priced = (f.currency === "IQD" || f.currency === "USD") && !badRange(f);
  return {
    area: f.area || undefined, currency: f.currency || undefined, price_min: priced && f.price_min ? f.price_min : undefined,
    price_max: priced && f.price_max ? f.price_max : undefined, sort: priced ? f.sort : undefined,
  };
};

export const EventFilters = ({ f, setF, gov, type }: { f: Filters; setF: (f: Filters) => void; gov: string; type?: EventType }) => {
  const { t, lang } = usePrefs();
  const [areas, setAreas] = useState<{ ar: string; en: string }[]>([]);
  const up = (patch: Partial<Filters>) => setF({ ...f, ...patch });
  const priced = f.currency === "IQD" || f.currency === "USD";

  useEffect(() => {
    api.get("/events/areas", { params: { governorate: gov || undefined } }).then((r) => setAreas(r.data)).catch(() => setAreas([]));
  }, [gov]);

  return (
    <div className="space-y-3 rounded-3xl border bg-card/60 p-3 sm:p-4" data-testid="event-filters">
      <p className="text-xs font-bold text-muted-foreground inline-flex items-center gap-1.5"><SlidersHorizontal className="h-3.5 w-3.5" />{t("b4.filters")}</p>
      <div className="flex flex-wrap gap-2">
        <select value={f.area} onChange={(e) => up({ area: e.target.value })} data-testid="filter-area" className={sel}>
          <option value="">{t("b4.allAreas")}</option>
          {[...areas].sort((x, y) => (lang === "ar" ? x.ar.localeCompare(y.ar, "ar") : (x.en || x.ar).localeCompare(y.en || y.ar))).map((a) => <option key={a.ar} value={a.ar}>{lang === "ar" ? a.ar : a.en || a.ar}</option>)}
        </select>
        <select value={f.currency} onChange={(e) => up({ currency: e.target.value as Filters["currency"], price_min: "", price_max: "", sort: "date" })} data-testid="filter-currency" className={sel}>
          <option value="">{t("b4.anyPrice")}</option>
          <option value="free">{t("b4.free")}</option>
          <option value="IQD">{t("b4.iqd")}</option>
          <option value="USD">{t("b4.usd")}</option>
        </select>
        {priced && (
          <>
            <input type="number" min={0} inputMode="decimal" dir="ltr" value={f.price_min} onChange={(e) => up({ price_min: e.target.value })} placeholder={`${t("b4.min")} ${f.currency}`} data-testid="filter-price-min" className={money} />
            <input type="number" min={0} inputMode="decimal" dir="ltr" value={f.price_max} onChange={(e) => up({ price_max: e.target.value })} placeholder={`${t("b4.max")} ${f.currency}`} data-testid="filter-price-max" className={money} />
            <select value={f.sort} onChange={(e) => up({ sort: e.target.value as Filters["sort"] })} data-testid="filter-sort" className={sel}>
              <option value="date">{t("b4.sortDate")}</option>
              <option value="price">{t("b4.sortPrice")}</option>
            </select>
          </>
        )}
      </div>
      {priced && badRange(f) && <p data-testid="filter-price-error" className="text-xs font-bold text-destructive">{t("b4.minMax")}</p>}
      {priced && <p data-testid="filter-currency-note" className="text-[11px] text-muted-foreground">{t("b4.currencyNote")}</p>}
    </div>
  );
};
